/**
 * Der Anlage-Dialog für Blacklists — Zustand und Verhalten an einer Stelle.
 *
 * Herausgelöst aus `global-blacklist-overview.tsx` (959 Zeilen), damit der Chat DENSELBEN
 * Dialog öffnen kann wie die Oberfläche. Genau das ist die Leitentscheidung des
 * Kampagnen-Umbaus: **ein Dialog, zwei Aufrufwege.** Eine zweite, im Chat nachgebaute
 * Blacklist-Maske wäre kein Fortschritt, sondern die nächste Stelle, an der zwei
 * Fassungen auseinanderdriften — bei einem Formular mit Datei-Upload, Spaltenauswahl und
 * Firmensuche umso schneller.
 *
 * Der Hook kennt die Seite nicht, auf der er hängt. Was danach passiert, entscheidet der
 * Aufrufer über `onCreated`: Die Übersicht lädt ihre Liste neu, der Chat schickt eine
 * Quittung ins Gespräch.
 *
 * Verschoben, nicht neu geschrieben: Der Rumpf ist wortgleich der bisherige. Wer hier
 * etwas ändern will, ändert es für beide Aufrufwege — das ist der Zweck.
 */

import { createGlobalBlacklist } from 'API/company-blacklist';
import { getLinkedinCompanies } from 'API/company-urn';
import { FileUploadMatchColumns } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import { parse } from 'papaparse';
import React, { useCallback, useState } from 'react';
import validator from 'validator';
import * as XLSX from 'xlsx';

import { BlacklistUploadVariant } from '../campaign-builder/campaign-builder.types';
import { Blacklist_New, BlacklistTypes } from './company-blacklist.types';

interface Optionen {
  /** Für die Vorauswahl des Kontos in der Firmensuche. */
  linkedinAccounts: LinkedinAccount[];
  /** Nach erfolgreicher Anlage — Liste neu laden, Quittung senden, was der Aufrufer braucht. */
  onCreated?: (name: string, type: BlacklistTypes, anzahl: number, id?: string) => void | Promise<void>;
  /** Fehler aus der Anlage. Der Aufrufer entscheidet, wie er ihn zeigt. */
  onError?: (error: unknown) => void;
  /** Der Wiederanmelde-Hinweis der Firmensuche — auf der Seite ein Fehlerdialog. */
  onNeedsRelogin?: () => void;
  /** Sprache fuer die Firmensuche. Der Hook liest keine i18n-Instanz. */
  sprache?: string;
}

export function useAddBlacklist({ linkedinAccounts, onCreated, onError, onNeedsRelogin, sprache = 'de' }: Optionen) {
  const [addBlacklistDialog, setAddBlacklistDialog] = useState<{
    open: boolean;
    loading: boolean;
    blacklist?: Blacklist_New | null;
    blacklistName: string;
    blacklistType: BlacklistTypes;
    blacklistUploadVariant: BlacklistUploadVariant;
    individualItemsTextField: string;
    individualItemsTextFieldError: true | null;
    individualItemsList: string[];
    fileColumnSelection: {
      columns: { text: string; type: string }[];
      data: string[][];
      fileNames: string[];
      hasTableHeading: boolean;
    };
    fileItemsList: { url?: string; company?: string }[];
    selectedLinkedinAccount: string;
    companyQuery: string;
    companyOptions: string[][];
    selectedCompanies: string[][];
    companyLoading: boolean;
    companyIsEmpty: boolean;
  }>({
    open: false,
    loading: false,
    blacklist: null,
    blacklistName: '',
    blacklistType: BlacklistTypes.CONTACT,
    blacklistUploadVariant: BlacklistUploadVariant.FILE,
    individualItemsTextField: '',
    individualItemsTextFieldError: null,
    individualItemsList: [],
    fileColumnSelection: {
      columns: [],
      data: [],
      fileNames: [],
      hasTableHeading: true,
    },
    fileItemsList: [],
    selectedLinkedinAccount: '',
    companyQuery: '',
    companyOptions: [],
    selectedCompanies: [],
    companyLoading: false,
    companyIsEmpty: false,
  });
  const [companyTypingTimer, setCompanyTypingTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleOpenAddBlacklistDialog = () => {
    setAddBlacklistDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseAddBlacklistDialog = () => {
    setAddBlacklistDialog({
      open: false,
      loading: false,
      blacklist: null,
      blacklistName: '',
      blacklistType: BlacklistTypes.CONTACT,
      blacklistUploadVariant: BlacklistUploadVariant.FILE,
      individualItemsTextField: '',
      individualItemsTextFieldError: null,
      individualItemsList: [],
      fileColumnSelection: {
        columns: [],
        data: [],
        fileNames: [],
        hasTableHeading: true,
      },
      fileItemsList: [],
      selectedLinkedinAccount: linkedinAccounts[0]?._id ?? '',
      companyQuery: '',
      companyOptions: [],
      selectedCompanies: [],
      companyLoading: false,
      companyIsEmpty: false,
    });
  };

  const handleBlacklistNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddBlacklistDialog((prev) => ({ ...prev, blacklistName: event.target.value }));
  };

  const handleCreateGlobalBlacklist = async () => {
    setAddBlacklistDialog((prev) => ({ ...prev, loading: true }));

    const { blacklistName, blacklistType, blacklistUploadVariant, individualItemsList, fileItemsList, selectedCompanies } = addBlacklistDialog;

    const newBlacklistItems =
      blacklistUploadVariant === BlacklistUploadVariant.INDIVIDUAL
        ? individualItemsList.map((item) => {
            if (blacklistType === BlacklistTypes.CONTACT) {
              return { contactUrl: item };
            } else {
              return { companyName: item, companyUrn: '' };
            }
          })
        : blacklistUploadVariant === BlacklistUploadVariant.FILE
        ? fileItemsList.map((item) => {
            if (blacklistType === BlacklistTypes.CONTACT) {
              return { contactUrl: item.url };
            } else {
              return { companyName: item.company, companyUrn: '' };
            }
          })
        : blacklistUploadVariant === BlacklistUploadVariant.LINKEDIN_COMPANY_PICKER
        ? selectedCompanies.map((item) => ({
            companyName: item[1],
            companyUrn: item[0],
          }))
        : [];

    const res = await createGlobalBlacklist(blacklistType, blacklistName, newBlacklistItems);

    if (!res.error) {
      // Erst schliessen, dann melden: Der Aufrufer laedt danach neu oder schickt eine
      // Quittung, und beides soll nicht auf ein offenes Fenster treffen.
      const anzahl = newBlacklistItems.length;
      handleCloseAddBlacklistDialog();
      await onCreated?.(blacklistName, blacklistType, anzahl, typeof res?._id === 'string' ? res._id : undefined);
    } else {
      setAddBlacklistDialog((prev) => ({ ...prev, loading: false }));
      onError?.(res?.error);
    }
  };

  /**
   * Blacklist type handlers
   */

  const handleBlacklistTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddBlacklistDialog((prev) => ({ ...prev, blacklistType: event.target.value as BlacklistTypes }));
    resetUploadValues();
    handleResetFileColumnSelection();
    handleResetBlacklistItems();
  };

  /**
   * Blacklist upload handlers
   */

  const handleBlacklistUploadVariantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddBlacklistDialog((prev) => ({ ...prev, blacklistUploadVariant: event.target.value as BlacklistUploadVariant }));
    resetUploadValues();
    handleResetFileColumnSelection();
    handleResetBlacklistItems();
  };

  /**
   * Upload contact / companies handlers
   */

  const resetUploadValues = () => {
    setAddBlacklistDialog((prev) => ({
      ...prev,
      individualItemsTextField: '',
      individualItemsTextFieldError: null,
      individualItemsList: [],
      companyQuery: '',
      companyOptions: [],
      selectedCompanies: [],
      companyIsEmpty: false,
    }));
  };

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddBlacklistDialog((prev) => ({ ...prev, individualItemsTextField: event.target.value, individualItemsTextFieldError: null }));
  };

  const isLinkedinContactUrl = (url: string) => {
    return validator.isURL(url) && url.includes('www.linkedin.com/in/');
  };

  const handleAddIndividualItem = () => {
    const { blacklistType, individualItemsTextField } = addBlacklistDialog;

    if (blacklistType === BlacklistTypes.CONTACT && !isLinkedinContactUrl(individualItemsTextField)) {
      setAddBlacklistDialog((prev) => ({ ...prev, individualItemsTextFieldError: true }));

      return;
    }

    setAddBlacklistDialog((prev) => {
      const updateDialog = { ...prev };
      updateDialog.individualItemsList.push(individualItemsTextField);
      updateDialog.individualItemsTextField = '';
      updateDialog.individualItemsTextFieldError = null;

      return updateDialog;
    });
  };

  const handlePasteIndividualItem = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');

    setAddBlacklistDialog((prev) => ({ ...prev, individualItemsTextField: pastedText }));

    const { blacklistType } = addBlacklistDialog;

    if (blacklistType === BlacklistTypes.CONTACT && !isLinkedinContactUrl(pastedText)) {
      setAddBlacklistDialog((prev) => ({ ...prev, individualItemsTextFieldError: true }));

      return;
    }

    setAddBlacklistDialog((prev) => {
      const updateDialog = { ...prev };
      updateDialog.individualItemsList.push(pastedText);
      updateDialog.individualItemsTextField = '';
      updateDialog.individualItemsTextFieldError = null;

      return updateDialog;
    });
  };

  const handleRemoveIndividualItem = (idx: number) => {
    return () => {
      setAddBlacklistDialog((prev) => {
        const updateDialog = { ...prev };
        updateDialog.individualItemsList.splice(idx, 1);

        return updateDialog;
      });
    };
  };

  const handleLinkedinAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddBlacklistDialog((prev) => ({
      ...prev,
      selectedLinkedinAccount: event.target.value,
      companyQuery: '',
      companyOptions: [],
      selectedCompanies: [],
      companyIsEmpty: false,
    }));
  };

  const handleCompanyQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddBlacklistDialog((prev) => ({
      ...prev,
      companyQuery: event.target.value,
      companyIsEmpty: false,
    }));
  };

  /**
   * Der Wiederanmelde-Hinweis gehoert dem Aufrufer.
   *
   * Frueher stand hier ein `window.confirm` samt Navigation in die Kontoverwaltung - das
   * ist Sache der Seite, nicht des Dialogs. Im Chat gaebe es diesen Weg gar nicht: Dort
   * ist die Kontoverwaltung kein Reiter, sondern ein eigener Ablauf.
   */
  const promptReloginForCompanyLookup = () => {
    onNeedsRelogin?.();
  };

  const handleSearchCompanies = async () => {
    const { companyQuery, selectedLinkedinAccount } = addBlacklistDialog;
    if (!selectedLinkedinAccount || companyQuery.trim() === '') {
      setAddBlacklistDialog((prev) => ({ ...prev, companyOptions: [] }));
      return;
    }

    const selectedAccount = linkedinAccounts.find((account) => account._id === selectedLinkedinAccount);
    if (!selectedAccount || String(selectedAccount.status) !== 'synchronized') {
      setAddBlacklistDialog((prev) => ({
        ...prev,
        companyOptions: [],
        companyIsEmpty: false,
      }));
      promptReloginForCompanyLookup();
      return;
    }

    setAddBlacklistDialog((prev) => ({ ...prev, companyLoading: true }));
    const lang = sprache;
    const res = await getLinkedinCompanies(companyQuery, lang, selectedLinkedinAccount);

    if (!res.error) {
      const options = Array.isArray(res) ? res : [];
      setAddBlacklistDialog((prev) => ({
        ...prev,
        companyOptions: options,
        companyIsEmpty: options.length === 0,
      }));
    } else {
      setAddBlacklistDialog((prev) => ({ ...prev, companyOptions: [] }));
      if ((res as any)?.error?.code === 'SESSION_INVALID') {
        // Der Hook besitzt die Kontoliste nicht - er meldet nur, dass die Sitzung
        // abgelaufen ist. Wer die Liste haelt, aktualisiert sie.
        promptReloginForCompanyLookup();
        setAddBlacklistDialog((prev) => ({ ...prev, companyLoading: false }));
        return;
      }
      onError?.(res?.error);
    }

    setAddBlacklistDialog((prev) => ({ ...prev, companyLoading: false }));
  };

  const searchCompanyKeyUp = () => {
    if (companyTypingTimer) {
      clearTimeout(companyTypingTimer);
    }

    if (addBlacklistDialog.companyQuery.trim() !== '') {
      const timer = setTimeout(() => {
        handleSearchCompanies();
      }, 1000);
      setCompanyTypingTimer(timer);
    } else {
      setAddBlacklistDialog((prev) => ({ ...prev, companyOptions: [] }));
    }
  };

  const searchCompanyKeyDown = () => {
    if (companyTypingTimer) {
      clearTimeout(companyTypingTimer);
    }
  };

  const handleToggleCompany = (value: string) => {
    return () => {
      setAddBlacklistDialog((prev) => {
        const selected = _.cloneDeep(prev.selectedCompanies);
        const selectedUrns = selected.map((item) => item[0]);
        if (selectedUrns.includes(value)) {
          _.remove(selected, (item) => item[0] === value);
        } else {
          const found = prev.companyOptions.find((item) => item[0] === value);
          if (!_.isNil(found)) {
            selected.push(found);
          }
        }
        return { ...prev, selectedCompanies: selected };
      });
    };
  };

  const handleRemoveCompany = (value: string) => {
    return () => {
      setAddBlacklistDialog((prev) => {
        const selected = _.cloneDeep(prev.selectedCompanies);
        _.remove(selected, (item) => item[0] === value);
        return { ...prev, selectedCompanies: selected };
      });
    };
  };

  /**
   * File upload handlers
   */

  const onFileDrop = useCallback((files: any[]) => {
    files.forEach((file: any) => {
      const reader = new FileReader();

      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');

      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        reader.onload = (e: any) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const parsedData: string[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (!_.isNil(parsedData)) {
            handleInitFileColumnSelection(parsedData, file.name);
          }
        };
        reader.readAsArrayBuffer(file);
      } else if (file.name.endsWith('.csv')) {
        reader.onload = (e: any) => {
          const text = e.target.result;
          const parsedData = parse(text, { delimiter: ';' });

          if (!_.isNil(parsedData.data)) {
            handleInitFileColumnSelection(parsedData.data as string[][], file.name);
          }
        };
        reader.readAsText(file);
      } else {
        console.log('Unsupported file type');
      }
    });
  }, []);

  const handleInitFileColumnSelection = (data: string[][], fileName: string) => {
    const columns = data[1] || data[0];
    if (!columns) return;
    const newColumns = Array.from({ length: columns.length }, (_, index) => ({
      text: columns[index] || '',
      type: '',
    }));

    setAddBlacklistDialog((prev) => ({
      ...prev,
      fileColumnSelection: {
        ...prev.fileColumnSelection,
        data: [...prev.fileColumnSelection.data, ...data],
        columns: newColumns,
        fileNames: [...prev.fileColumnSelection.fileNames, fileName],
      },
    }));
  };

  const handleColumnTypeChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addBlacklistDialog };

      if (event.target.value === FileUploadMatchColumns.EMPTY) {
        updateDialog.fileColumnSelection.columns[idx].type = '';
      } else {
        const existingSameType = updateDialog.fileColumnSelection.columns.find((col) => col?.type === event.target.value);

        if (!_.isNil(existingSameType)) {
          existingSameType.type = '';
        }

        updateDialog.fileColumnSelection.columns[idx].type = event.target.value;
      }

      setAddBlacklistDialog(updateDialog);
    };
  };

  const handleHasTableHeadingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { fileColumnSelection } = addBlacklistDialog;
    const { data, columns } = fileColumnSelection;

    const columnStrings = data[event.target.checked ? 1 : 0];

    if (!columnStrings) return;

    let newColumns: { text: string; type: string }[] = [];

    columnStrings.forEach((col, index) => {
      const updateColumns = [...columns];
      updateColumns[index].text = col;
      newColumns = updateColumns;
    });

    setAddBlacklistDialog((prev) => ({
      ...prev,
      fileColumnSelection: { ...prev.fileColumnSelection, columns: newColumns, hasTableHeading: event.target.checked },
    }));
  };

  const handleResetFileColumnSelection = () => {
    setAddBlacklistDialog((prev) => ({
      ...prev,
      fileColumnSelection: { data: [], columns: [], fileNames: [], hasTableHeading: true },
    }));
  };

  const handleResetBlacklistItems = () => {
    setAddBlacklistDialog((prev) => ({
      ...prev,
      fileItemsList: [],
    }));
  };

  const handleAcceptColumns = () => {
    const { fileColumnSelection } = addBlacklistDialog;
    const { data, columns, hasTableHeading } = fileColumnSelection;

    const transformedData: {
      url?: string;
      company?: string;
    }[] = data.map((row: string[]) => {
      const urlIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.LINKEDIN_URL);
      const companyIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.COMPANY);

      let contactUrl: string | undefined = undefined;
      let contactCompany: string | undefined = undefined;

      if (!_.isNil(urlIndex) && urlIndex >= 0) {
        contactUrl = row[urlIndex];
      }

      if (!_.isNil(companyIndex) && companyIndex >= 0) {
        contactCompany = row[companyIndex];
      }

      return {
        url: contactUrl,
        company: contactCompany,
      };
    });

    const items = hasTableHeading ? _.tail(transformedData) : transformedData;
    const hasUrlColumn = items.some((contact) => !!contact.url);
    const hasCompanyColumn = items.some((contact) => !!contact.company);
    let filteredItems = items;

    if (hasUrlColumn) {
      filteredItems = items.filter((item) => item.url && isLinkedinContactUrl(item.url));
    }
    if (hasCompanyColumn) {
      filteredItems = items.filter((item) => item.company);
    }
    filteredItems = _.uniqBy(filteredItems, hasUrlColumn ? 'url' : 'company');

    setAddBlacklistDialog((prev) => ({
      ...prev,
      fileItemsList: [...prev.fileItemsList, ...filteredItems],
    }));
    handleResetFileColumnSelection();
  };

  return {
    /** Zustand und Handler, fertig zum Ausbreiten in <AddBlacklistDialog>. */
    dialogProps: {
      open: addBlacklistDialog.open,
      loading: addBlacklistDialog.loading,
      blacklist: addBlacklistDialog.blacklist,
      blacklistName: addBlacklistDialog.blacklistName,
      blacklistType: addBlacklistDialog.blacklistType,
      blacklistUploadVariant: addBlacklistDialog.blacklistUploadVariant,
      individualItemsTextField: addBlacklistDialog.individualItemsTextField,
      individualItemsTextFieldError: addBlacklistDialog.individualItemsTextFieldError,
      individualItemsList: addBlacklistDialog.individualItemsList,
      fileColumnSelection: addBlacklistDialog.fileColumnSelection,
      blacklistItems: addBlacklistDialog.fileItemsList,
      linkedinAccounts,
      selectedLinkedinAccount: addBlacklistDialog.selectedLinkedinAccount,
      companyQuery: addBlacklistDialog.companyQuery,
      companyOptions: addBlacklistDialog.companyOptions,
      selectedCompanies: addBlacklistDialog.selectedCompanies,
      companyLoading: addBlacklistDialog.companyLoading,
      companyIsEmpty: addBlacklistDialog.companyIsEmpty,
      handleBlacklistNameChange,
      handleBlacklistTypeChange,
      handleBlacklistUploadVariantChange,
      onFileDrop,
      handleColumnTypeChange,
      handleHasTableHeadingChange,
      handleAcceptColumns,
      handleResetBlacklistItems,
      handleResetFileColumnSelection,
      handleTextFieldChange,
      handleAddIndividualItem,
      handlePasteIndividualItem,
      handleRemoveIndividualItem,
      handleLinkedinAccountChange,
      handleCompanyQueryChange,
      searchCompanyKeyUp,
      searchCompanyKeyDown,
      handleToggleCompany,
      handleRemoveCompany,
      handleClose: handleCloseAddBlacklistDialog,
      handleCreate: handleCreateGlobalBlacklist,
    },
    open: handleOpenAddBlacklistDialog,
    close: handleCloseAddBlacklistDialog,
    isOpen: addBlacklistDialog.open,
    /**
     * Rohzustand und Setter — fuer Aufrufer, die mehr tun als anlegen.
     *
     * Die Uebersicht benutzt denselben Dialog auch zum BEARBEITEN einer bestehenden
     * Liste; sie oeffnet ihn dafuer mit gefuelltem `blacklist`. Diesen Weg hier
     * nachzubauen waere die zweite Fassung, die wir gerade abschaffen - also gibt der
     * Hook den Zustand heraus, statt ihn zu verstecken.
     */
    state: addBlacklistDialog,
    setState: setAddBlacklistDialog,
  };
}
